import React, { useEffect, useState } from 'react';

const STALL_TIMEOUT_MS = 12_000;

interface GuardFallbackProps {
  /** Session-resolution error message, if the users.me query failed. */
  error: string | null;
  /** Re-runs the users.me query (transient failures recover without a reload). */
  onRetry: () => void;
}

/**
 * Loading/error surface for the route guards (BrandAdminRoute, PortalRoute,
 * FinanceRoute, V2PreviewRoute).
 *
 * Before 2026-08-05 the guards rendered a bare spinner with no error branch
 * and no timeout — any stall in session resolution (a Clerk hydration hang,
 * or the users.me query parked in error state) spun forever with a silent
 * console, which repeatedly blocked visual verification. A query error now
 * renders a retry panel immediately, and a spinner older than
 * STALL_TIMEOUT_MS escalates to a reload panel.
 */
export const GuardFallback: React.FC<GuardFallbackProps> = ({ error, onRetry }) => {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (error) return undefined;
    const timer = setTimeout(() => setStalled(true), STALL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [error]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <p className="text-gray-700 font-medium">We couldn&apos;t load your session.</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navyHover"
        >
          Try again
        </button>
      </div>
    );
  }

  if (stalled) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <p className="text-gray-700 font-medium">
          Loading your session is taking longer than it should.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navyHover"
        >
          Reload the page
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
    </div>
  );
};

export default GuardFallback;
